/**
 * Roster-aware leave hours resolution — shared types, normaliser, labels and
 * a pure mirror of the SQL rule (`public.resolve_leave_request_hours`,
 * migration 20271036000060).
 *
 * The SQL function is the authority at submission time; this mirror exists
 * so the rule can be unit-tested (mutation-proven) without a database and so
 * every surface renders the result through one normaliser and one label map.
 *
 * Rule (note 14 amended 2026-08-30; note 15 primary sources): leave HOURS for
 * a day are the worker's ORDINARY hours of work for that day (RACV [2015]
 * FWCFB 2881 [82]–[85]). Public holidays inside the window are not leave
 * (FW Act s 89 / s 98). In order, over the window with public-holiday dates
 * removed:
 *   1. pay_profile     written agreed pattern (contracted hours ÷ |usual
 *                      weekday set|, capped at maximum daily ordinary hours),
 *                      counted over that same set;
 *   2. roster          published, non-overtime assignments inside the window,
 *                      each day capped at maximum daily ordinary hours; days
 *                      after the roster horizon fall through to 3–5;
 *   3. roster_history  the established roster cycle: median weekly ordinary
 *                      hours over the last ≤ 8 published weeks (≥ 3 weeks),
 *                      ÷ and counted over the same usual weekday set;
 *   4. custom_fields   staff record daily hours (8 or 10) × business days;
 *   5. default         7.6 × business days.
 * Casual employment keeps the legacy behaviour (per-day × business days).
 *
 * USUAL WEEKDAY SET — one set drives BOTH the weekly divisor and the days
 * counted in the window: profile `ordinary_days_of_week` when non-empty, else
 * the roster-history usual weekdays (≥ 3 sampled weeks). A contracted weekly
 * total with neither source is held for review: Mon–Fri is not evidence of a
 * part-time or shift worker's ordinary pattern. The divisor is that set's
 * cardinality, never a separately-sourced number.
 * Sourcing them apart overstated a Tue/Thu/Sat 24 h/week part-timer's Mon–Fri
 * week as 40 h instead of 16 h. Steps 4–5 carry no weekly divisor and keep the
 * Mon–Fri business-day count.
 *
 * DELIBERATE ASYMMETRY — an unrostered day INSIDE the roster horizon is 0 h; a
 * day BEYOND it is filled from steps 3–5. A published roster is evidence of
 * ordinary hours, so a published date with no shift is evidence of no ordinary
 * hours that day; beyond the horizon there is no roster to be evidence of
 * anything. See note 14.
 *
 * TOP-LEVEL `source` names the basis that produced the MAJORITY of the hours
 * (ties go to the LOWER-precedence, more uncertain basis — never over-claim);
 * `detail.source_basis` carries the split.
 */

import { HOURS_PER_LEAVE_DAY } from './leave-hours-constants';
import { rosteredLeaveHoursFromSpan } from './leave-pay-treatment';

export const LEAVE_HOURS_SOURCES = [
  'roster',
  'pay_profile',
  'roster_history',
  'custom_fields',
  'default',
] as const;

export type LeaveHoursSource = (typeof LEAVE_HOURS_SOURCES)[number];

/**
 * Persisted-only token for hours a person typed (coordinator single-day
 * override, partial day). The resolver never emits it.
 */
export type LeaveHoursSegmentSource = LeaveHoursSource | 'manual';

export const LEAVE_HOURS_BLOCKED_REASONS = [
  'pay_profile_transition_in_window',
  'ordinary_weekday_pattern_unverified',
  'public_holiday_work_state_unverified',
  'public_holiday_work_location_unverified',
  'cross_midnight_slot_date_unverified',
  'no_ordinary_hours_in_window',
  'roster_coverage_split_window',
  'roster_coverage_disjoint_window',
  'default_hours_unverified',
  /** No dated roster assignment and no agreed daily pattern. Never infer 7.6 h. */
  'contracted_daily_hours_unverified',
  'resolver_review_required',
] as const;
export type LeaveHoursBlockedReason = (typeof LEAVE_HOURS_BLOCKED_REASONS)[number];

const LEAVE_HOURS_SOURCE_SET: ReadonlySet<string> = new Set(LEAVE_HOURS_SOURCES);

export function isLeaveHoursSource(value: unknown): value is LeaveHoursSource {
  return typeof value === 'string' && LEAVE_HOURS_SOURCE_SET.has(value);
}

/**
 * Display labels for the source token. Both apps' enum formatters consult
 * this map so the raw token never reaches a screen.
 */
export const LEAVE_HOURS_SOURCE_LABELS: Record<LeaveHoursSegmentSource, string> = {
  roster: 'Published roster',
  pay_profile: 'Agreed hours (pay profile)',
  roster_history: 'Usual roster pattern',
  custom_fields: 'Staff record daily hours',
  default: '7.6 h default',
  manual: 'Entered manually',
};

export function leaveHoursSourceLabel(value: string | null | undefined): string {
  if (!value) return LEAVE_HOURS_SOURCE_LABELS.default;
  return (
    LEAVE_HOURS_SOURCE_LABELS[value as LeaveHoursSegmentSource] ??
    value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/**
 * Coordinator helper copy when the rule fell through to the default: there is
 * no roster evidence and no agreed pattern, so the figure must be confirmed.
 */
export const LEAVE_HOURS_DEFAULT_SOURCE_HELPER =
  'Hours from the 7.6 h default — no roster or agreed pattern found. Confirm before approving.';

export interface LeaveHoursResolutionDay {
  date: string;
  hours: number;
  source: LeaveHoursSource;
  rosterShiftId: string | null;
  rosterAssignmentId: string | null;
  slotId: string | null;
  segmentStartAt: string | null;
  segmentEndAt: string | null;
  /** Roster shift the hours came from even when the day was capped/unlinked. */
  evidenceShiftId: string | null;
  cappedToOrdinary: boolean;
}

export interface LeaveHoursResolution {
  hours: number;
  source: LeaveHoursSource;
  perDay: LeaveHoursResolutionDay[];
  /** Distinct dates carrying hours. */
  days: number;
  /** Monday–Friday dates in the window (before public-holiday exclusion). */
  businessDays: number;
  publicHolidayDates: string[];
  hoursPerLeaveDay: number;
  /** Safe hold: the resolver refused to invent hours for this window. */
  blockedReason: LeaveHoursBlockedReason | null;
  detail: Record<string, unknown>;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function numberOr(value: unknown, fallback: number): number {
  if (value === null || value === undefined || value === '') return fallback;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function isIsoCalendarDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year!, month! - 1, day!));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() + 1 === month &&
    parsed.getUTCDate() === day
  );
}

/**
 * Normalise the SQL payload (`leave_hours_resolution` on the windowed balance
 * summary, or the direct `resolve_leave_request_hours` result). Returns null
 * for an absent/invalid payload so callers can fall back to the legacy
 * per-day factor rather than rendering an invented figure.
 */
export function normalizeLeaveHoursResolution(
  input: unknown,
  expectedWindow?: { startDate: string; endDate: string },
): LeaveHoursResolution | null {
  if (input === null || input === undefined) return null;
  const record = asRecord(input);
  if (!('hours' in record) || !isLeaveHoursSource(record.source)) return null;
  const rawHours = typeof record.hours === 'number' ? record.hours : Number(record.hours);
  if (!Number.isFinite(rawHours) || rawHours < 0) return null;
  const perDayValue = record.per_day ?? record.perDay;
  if (perDayValue !== undefined && !Array.isArray(perDayValue)) return null;
  const perDayRaw = (perDayValue ?? []) as unknown[];
  const perDay: LeaveHoursResolutionDay[] = [];
  for (const rawEntry of perDayRaw) {
    const entry = asRecord(rawEntry);
    const date = typeof entry.date === 'string' ? entry.date : null;
    const dayHours = typeof entry.hours === 'number' ? entry.hours : Number(entry.hours);
    if (
      !date ||
      !isIsoCalendarDate(date) ||
      (expectedWindow && (date < expectedWindow.startDate || date > expectedWindow.endDate)) ||
      !Number.isFinite(dayHours) ||
      dayHours <= 0
    ) {
      return null;
    }
    if (!isLeaveHoursSource(entry.source)) return null;
    const rosterShiftId = stringOrNull(entry.roster_shift_id ?? entry.rosterShiftId);
    const rosterAssignmentId = stringOrNull(entry.roster_assignment_id ?? entry.rosterAssignmentId);
    const slotId = stringOrNull(entry.slot_id ?? entry.slotId);
    const linkageCount = [rosterShiftId, rosterAssignmentId, slotId].filter(Boolean).length;
    const segmentStartAt = stringOrNull(entry.segment_start_at ?? entry.segmentStartAt);
    const segmentEndAt = stringOrNull(entry.segment_end_at ?? entry.segmentEndAt);
    if (
      (linkageCount !== 0 && linkageCount !== 3) ||
      (segmentStartAt === null) !== (segmentEndAt === null) ||
      (linkageCount === 3 && (segmentStartAt === null || segmentEndAt === null))
    ) {
      return null;
    }
    perDay.push({
      date,
      hours: dayHours,
      source: entry.source,
      rosterShiftId,
      rosterAssignmentId,
      slotId,
      segmentStartAt,
      segmentEndAt,
      evidenceShiftId: stringOrNull(entry.evidence_shift_id ?? entry.evidenceShiftId),
      cappedToOrdinary: entry.capped_to_ordinary === true || entry.cappedToOrdinary === true,
    });
  }
  const publicHolidayDates = Array.isArray(record.public_holiday_dates ?? record.publicHolidayDates)
    ? ((record.public_holiday_dates ?? record.publicHolidayDates) as unknown[])
        .filter((d): d is string => typeof d === 'string')
        .map((d) => d.slice(0, 10))
    : [];
  const hours = rawHours;
  const days = Math.round(numberOr(record.days, perDay.length));
  const rowHours = perDay.reduce((sum, day) => sum + day.hours, 0);
  const distinctDays = new Set(perDay.map((day) => day.date)).size;
  if (
    (hours > 0 && perDay.length === 0) ||
    (hours === 0 && perDay.length > 0) ||
    Math.round(rowHours * 100) !== Math.round(hours * 100) ||
    days !== distinctDays
  ) {
    return null;
  }
  return {
    hours,
    source: record.source,
    perDay,
    days,
    businessDays: Math.round(numberOr(record.business_days ?? record.businessDays, 0)),
    publicHolidayDates,
    hoursPerLeaveDay: numberOr(
      record.hours_per_leave_day ?? record.hoursPerLeaveDay,
      days > 0 ? hours / days : HOURS_PER_LEAVE_DAY,
    ),
    blockedReason: (() => {
      const raw = String(record.blocked_reason ?? record.blockedReason ?? '').trim();
      if (!raw) return null;
      return LEAVE_HOURS_BLOCKED_REASONS.includes(raw as LeaveHoursBlockedReason)
        ? (raw as LeaveHoursBlockedReason)
        : 'resolver_review_required';
    })(),
    detail: asRecord(record.detail),
  };
}

/**
 * One-line, tenant-timezone-free description for the source line shown
 * wherever hours are shown for a request. Dates are ISO calendar dates
 * (leave windows are DATE columns); callers format them for display.
 */
export function describeLeaveHoursResolution(resolution: LeaveHoursResolution): string {
  const label = leaveHoursSourceLabel(resolution.source);
  const parts: string[] = [];
  if (resolution.source === 'roster') {
    const roster = asRecord(resolution.detail.roster);
    const shiftCount = Math.round(numberOr(roster.shift_count, 0));
    if (shiftCount > 0) parts.push(`${shiftCount} rostered shift${shiftCount === 1 ? '' : 's'}`);
    const beyond = asRecord(roster.beyond_horizon);
    if (Object.keys(beyond).length > 0) {
      parts.push(
        `then ${leaveHoursSourceLabel(String(beyond.source ?? 'default')).toLowerCase()} from ${String(beyond.from ?? '')}`,
      );
    }
    const capped = Math.round(numberOr(roster.days_capped_to_ordinary, 0));
    if (capped > 0) parts.push(`${capped} day${capped === 1 ? '' : 's'} capped at ordinary hours`);
  } else if (resolution.source === 'roster_history') {
    const history = asRecord(resolution.detail.history);
    const weeks = Math.round(numberOr(history.weeks, 0));
    const median = numberOr(history.median_hours_per_week, 0);
    if (weeks > 0) parts.push(`median of ${weeks} published week${weeks === 1 ? '' : 's'}`);
    if (median > 0) parts.push(`${median} h/week`);
  } else if (resolution.source === 'pay_profile') {
    const pattern = asRecord(resolution.detail.pattern);
    const contracted = numberOr(pattern.contracted_hours_per_week, 0);
    const usualDays = numberOr(pattern.usual_days_per_week, 0);
    if (contracted > 0) parts.push(`${contracted} h/week`);
    if (usualDays > 0) parts.push(`${usualDays} usual day${usualDays === 1 ? '' : 's'}/week`);
  }
  if (resolution.publicHolidayDates.length > 0) {
    parts.push(
      `${resolution.publicHolidayDates.length} public holiday${
        resolution.publicHolidayDates.length === 1 ? '' : 's'
      } excluded`,
    );
  }
  return parts.length > 0 ? `${label} (${parts.join('; ')})` : label;
}

// ---------------------------------------------------------------------------
// Pure mirror of the SQL rule.
// ---------------------------------------------------------------------------

export interface LeaveHoursRosterAssignmentFact {
  /** Authoritative slot-local tenant calendar date (YYYY-MM-DD). */
  date: string;
  /** Parent roster_shifts.date, retained only to detect an overnight split. */
  parentShiftDate?: string | null;
  /** Slot hours net of break, 4 dp, unless span/break inputs are supplied. */
  hours: number;
  /** Raw shift span minutes; when set with breakMinutes, hours are recomputed. */
  spanMinutes?: number | null;
  breakMinutes?: number | null;
  /** ACTIVE pay-rule block `countsForLeave` for LEAVE_REQUEST (shiftworker meal). */
  leaveBreakCountsForLeave?: boolean | null;
  triggerAfterContinuousMinutes?: number | null;
  isOvertime?: boolean;
  shiftId?: string | null;
  assignmentId?: string | null;
  slotId?: string | null;
  startAt?: string | null;
  endAt?: string | null;
  /** Shift date present and assignment period matches the shift period. */
  provenanceOk?: boolean;
}

export interface LeaveHoursHistoryWeekFact {
  /** ISO Monday of the week. */
  weekStart: string;
  /** Per-day ordinary hours (overtime excluded); the mirror applies the cap. */
  days: Array<{ date: string; hours: number }>;
}

export interface LeaveHoursResolutionFacts {
  startDate: string;
  endDate: string;
  employmentType?: string | null;
  contractedHoursPerWeek?: number | null;
  maximumDailyOrdinaryHours?: number | null;
  /**
   * The ordinary-hours profile's `ordinary_days_of_week` (ISO day numbers,
   * 1 = Monday … 7 = Sunday). This is the FIRST source of the one usual
   * weekday set that drives both the weekly divisor and the counted days.
   */
  ordinaryDaysOfWeek?: readonly number[] | null;
  /**
   * @deprecated Kept so existing callers still typecheck. Ignored when
   * `ordinaryDaysOfWeek` is supplied; a bare count cannot say WHICH days the
   * worker works, and dividing by it while counting Mon–Fri is exactly the
   * defect this field's replacement fixes.
   */
  ordinaryDaysOfWeekCount?: number | null;
  customFullTimeDailyOrdinaryHours?: number | null;
  publicHolidayDates?: readonly string[];
  /** Published (non-cancelled) assignments inside the window. */
  rosterAssignments?: readonly LeaveHoursRosterAssignmentFact[];
  /** Last published shift date anywhere for the worker. */
  rosterHorizonDate?: string | null;
  /** Start/end of published roster-period coverage for this worker. */
  rosterCoverageStartDate?: string | null;
  rosterCoverageEndDate?: string | null;
  /** Number of uncovered gaps between otherwise relevant published periods. */
  rosterCoverageGapCount?: number;
  /** Safe hold when effective pay-profile facts change inside the window. */
  profileTransitionInWindow?: boolean;
  /** A state/local holiday exists but the worker's state of work is unresolved. */
  publicHolidayWorkStateUnverified?: boolean;
  /** Roster and base locations disagree on holiday treatment for this window. */
  publicHolidayWorkLocationUnverified?: boolean;
  /** Sampled history weeks, most recent ≤ 8 with ≥ 1 assignment. */
  historyWeeks?: readonly LeaveHoursHistoryWeekFact[];
  /**
   * Compatibility-only opt-in for historical/report callers that still need
   * the former roster-history/custom-field/7.6 h ladder. Leave submission
   * never opts in: it must hold rather than invent a daily debit.
   */
  allowLegacyFallbacks?: boolean;
}

export const LEAVE_HOURS_HISTORY_SAMPLE_WEEKS = 8;
export const LEAVE_HOURS_HISTORY_MIN_WEEKS = 3;
const WEEKDAYS = [1, 2, 3, 4, 5] as const;

function round4(value: number): number {
  return Math.round(value * 10_000) / 10_000;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function dateUtcMs(value: string): number {
  const [y, m, d] = value.split('-').map(Number);
  return Date.UTC(y!, m! - 1, d!);
}

/**
 * Calendar-date arithmetic on YYYY-MM-DD strings (leave windows are DATE
 * columns, tz-free): the Date is UTC-anchored at midnight and read back with
 * the UTC accessors, so no local-offset shift can occur.
 */
function ymdFromUtcMs(ms: number): string {
  const date = new Date(ms);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** ISO day of week: Monday = 1 … Sunday = 7 (mirrors EXTRACT(ISODOW)). */
export function isoDayOfWeek(date: string): number {
  const day = new Date(dateUtcMs(date)).getUTCDay();
  return day === 0 ? 7 : day;
}

export function enumerateDates(startDate: string, endDate: string): string[] {
  const out: string[] = [];
  const end = dateUtcMs(endDate);
  for (let ms = dateUtcMs(startDate); ms <= end; ms += 86_400_000) out.push(ymdFromUtcMs(ms));
  return out;
}

function addDays(date: string, days: number): string {
  return ymdFromUtcMs(dateUtcMs(date) + days * 86_400_000);
}

/** percentile_cont(0.5): the continuous median (mean of the two middle values). */
export function continuousMedian(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/** Weekdays present in at least half of the sampled weeks (SQL: present*2 >= n). */
export function usualWeekdays(weeks: ReadonlyArray<{ weekdays: readonly number[] }>): number[] {
  const counts = new Map<number, number>();
  for (const week of weeks) {
    for (const dow of new Set(week.weekdays)) counts.set(dow, (counts.get(dow) ?? 0) + 1);
  }
  return [...counts.entries()]
    .filter(([, present]) => present * 2 >= weeks.length)
    .map(([dow]) => dow)
    .sort((a, b) => a - b);
}

interface PatternResult {
  source: LeaveHoursSource;
  perDay: number;
  usualDays: number;
  weekdays: number[];
  rows: LeaveHoursResolutionDay[];
  hours: number;
  days: number;
  countedDays: number;
  proportional: boolean;
  detail: Record<string, unknown>;
}

/**
 * Resolution ladder, most certain first. Used only to break an exact majority
 * tie for the top-level `source` label: the later entry (the more uncertain
 * basis) wins, so the label never over-claims. Mirrors c_source_precedence.
 */
export const LEAVE_HOURS_SOURCE_PRECEDENCE: readonly LeaveHoursSource[] = [
  'pay_profile',
  'roster',
  'roster_history',
  'custom_fields',
  'default',
];

export function leaveHoursSourcePrecedence(source: LeaveHoursSource): number {
  const index = LEAVE_HOURS_SOURCE_PRECEDENCE.indexOf(source);
  return index === -1 ? LEAVE_HOURS_SOURCE_PRECEDENCE.length : index;
}

function rosterAssignmentLeaveHours(assignment: LeaveHoursRosterAssignmentFact): number {
  if (
    assignment.spanMinutes != null &&
    assignment.breakMinutes != null &&
    Number.isFinite(assignment.spanMinutes) &&
    Number.isFinite(assignment.breakMinutes)
  ) {
    return rosteredLeaveHoursFromSpan({
      spanMinutes: assignment.spanMinutes,
      breakMinutes: assignment.breakMinutes,
      countsForLeave: assignment.leaveBreakCountsForLeave === true,
      triggerAfterContinuousMinutes: assignment.triggerAfterContinuousMinutes,
    });
  }
  return assignment.hours;
}

function dayRow(date: string, hours: number, source: LeaveHoursSource): LeaveHoursResolutionDay {
  return {
    date,
    hours,
    source,
    rosterShiftId: null,
    rosterAssignmentId: null,
    slotId: null,
    segmentStartAt: null,
    segmentEndAt: null,
    evidenceShiftId: null,
    cappedToOrdinary: false,
  };
}

/**
 * Medea leave rule (requirement 5): resolve each requested calendar day from
 * its actual roster assignment first. Inside published roster coverage, an
 * unassigned day is a zero-hour day; only dates outside coverage may use the
 * dated, agreed ordinary-hours pattern. The historic pattern/default ladder
 * is deliberately unavailable unless a non-leave caller explicitly opts in.
 */
export function resolveLeaveRequestHours(facts: LeaveHoursResolutionFacts): LeaveHoursResolution {
  if (facts.allowLegacyFallbacks === true) return resolveLeaveRequestHoursLegacy(facts);
  if (facts.endDate < facts.startDate) throw new Error('leave_hours_resolution_window_invalid');

  const phDates = new Set((facts.publicHolidayDates ?? []).map((date) => date.slice(0, 10)));
  const allDates = enumerateDates(facts.startDate, facts.endDate);
  const businessDays = allDates.filter((date) => isoDayOfWeek(date) <= 5).length;
  const profileWeekdays = [
    ...new Set(
      (facts.ordinaryDaysOfWeek ?? []).filter(
        (dow) => Number.isInteger(dow) && dow >= 1 && dow <= 7,
      ),
    ),
  ].sort((a, b) => a - b);
  const contracted =
    facts.contractedHoursPerWeek != null && facts.contractedHoursPerWeek > 0
      ? facts.contractedHoursPerWeek
      : null;
  const maxDaily =
    facts.maximumDailyOrdinaryHours != null && facts.maximumDailyOrdinaryHours > 0
      ? facts.maximumDailyOrdinaryHours
      : null;
  const profileDays = profileWeekdays.length;
  const contractedDaily =
    contracted !== null && profileWeekdays.length > 0
      ? round4(
          maxDaily === null
            ? contracted / profileWeekdays.length
            : Math.min(contracted / profileWeekdays.length, maxDaily),
        )
      : null;
  const employmentType = String(facts.employmentType ?? '').toUpperCase();
  const customDaily =
    employmentType === 'FULL_TIME' &&
    (facts.customFullTimeDailyOrdinaryHours === 8 || facts.customFullTimeDailyOrdinaryHours === 10)
      ? facts.customFullTimeDailyOrdinaryHours
      : null;
  const legacyPerDay =
    contracted !== null && profileDays > 0
      ? contractedDaily!
      : (customDaily ?? HOURS_PER_LEAVE_DAY);
  const legacySource: LeaveHoursSource =
    contracted !== null && profileDays > 0
      ? 'pay_profile'
      : customDaily !== null
        ? 'custom_fields'
        : 'default';
  const hold = (blockedReason: LeaveHoursBlockedReason, source: LeaveHoursSource = 'pay_profile') =>
    ({
      hours: 0,
      source,
      perDay: [],
      days: 0,
      businessDays,
      publicHolidayDates: [...phDates].sort(),
      hoursPerLeaveDay: legacyPerDay,
      blockedReason,
      detail: {
        window: { start: facts.startDate, end: facts.endDate },
        review_required: blockedReason,
        contracted_hours_per_week: contracted,
        profile_ordinary_days_of_week: profileWeekdays,
      },
    }) satisfies LeaveHoursResolution;

  if (facts.publicHolidayWorkStateUnverified === true)
    return hold('public_holiday_work_state_unverified');
  if (facts.publicHolidayWorkLocationUnverified === true)
    return hold('public_holiday_work_location_unverified');
  if (
    (facts.rosterAssignments ?? []).some(
      (assignment) =>
        assignment.parentShiftDate != null && assignment.parentShiftDate !== assignment.date,
    )
  ) {
    return hold('cross_midnight_slot_date_unverified', 'roster');
  }
  if (facts.profileTransitionInWindow === true) return hold('pay_profile_transition_in_window');

  // The SQL resolver retains this live branch verbatim. Casual leave remains
  // outside the Medea roster-hours change.
  if (employmentType === 'CASUAL') {
    const rows = allDates
      .filter((date) => isoDayOfWeek(date) <= 5 && !phDates.has(date))
      .map((date) => dayRow(date, round4(legacyPerDay), legacySource));
    return {
      hours: round2(rows.length * legacyPerDay),
      source: legacySource,
      perDay: rows,
      days: rows.length,
      businessDays,
      publicHolidayDates: [...phDates].sort(),
      hoursPerLeaveDay: round4(legacyPerDay),
      blockedReason: legacySource === 'default' ? 'default_hours_unverified' : null,
      detail: { casual: true, employment_type: facts.employmentType ?? null },
    };
  }

  const assignmentsByDate = new Map<string, LeaveHoursRosterAssignmentFact[]>();
  for (const assignment of facts.rosterAssignments ?? []) {
    if (
      assignment.date < facts.startDate ||
      assignment.date > facts.endDate ||
      phDates.has(assignment.date) ||
      assignment.isOvertime === true ||
      rosterAssignmentLeaveHours(assignment) <= 0
    )
      continue;
    const dayAssignments = assignmentsByDate.get(assignment.date) ?? [];
    dayAssignments.push(assignment);
    assignmentsByDate.set(assignment.date, dayAssignments);
  }

  const rosterRows: LeaveHoursResolutionDay[] = [];
  let rosterHours = 0;
  let overtimeExcluded = 0;
  for (const assignment of facts.rosterAssignments ?? []) {
    if (
      assignment.date >= facts.startDate &&
      assignment.date <= facts.endDate &&
      assignment.isOvertime === true
    )
      overtimeExcluded += 1;
  }

  for (const date of allDates) {
    if (phDates.has(date)) continue; // FW Act s 89/s 98: a public holiday is not leave.
    const assigned = assignmentsByDate.get(date) ?? [];
    if (assigned.length > 0) {
      const ordered = [...assigned].sort((left, right) =>
        String(left.startAt ?? '').localeCompare(String(right.startAt ?? '')),
      );
      const rawHours = ordered.reduce(
        (sum, assignment) => sum + rosterAssignmentLeaveHours(assignment),
        0,
      );
      const hours = round4(maxDaily === null ? rawHours : Math.min(rawHours, maxDaily));
      const only = ordered.length === 1 && hours === rawHours ? ordered[0]! : null;
      rosterRows.push({
        date,
        hours,
        source: 'roster',
        rosterShiftId: only?.provenanceOk === true ? (only.shiftId ?? null) : null,
        rosterAssignmentId: only?.provenanceOk === true ? (only.assignmentId ?? null) : null,
        slotId: only?.provenanceOk === true ? (only.slotId ?? null) : null,
        segmentStartAt: only?.provenanceOk === true ? (only.startAt ?? null) : null,
        segmentEndAt: only?.provenanceOk === true ? (only.endAt ?? null) : null,
        evidenceShiftId: only?.shiftId ?? ordered[0]?.shiftId ?? null,
        cappedToOrdinary: hours < rawHours,
      });
      rosterHours += hours;
    }
  }

  const coverageStart = facts.rosterCoverageStartDate ?? null;
  const coverageEnd = facts.rosterCoverageEndDate ?? facts.rosterHorizonDate ?? null;
  const hasPublishedCoverage = Boolean(
    coverageStart &&
    coverageEnd &&
    coverageStart <= facts.endDate &&
    coverageEnd >= facts.startDate,
  );
  if (hasPublishedCoverage && (facts.rosterCoverageGapCount ?? 0) > 0) {
    return hold('roster_coverage_disjoint_window', 'roster');
  }
  if (hasPublishedCoverage && facts.startDate < coverageStart! && facts.endDate > coverageEnd!) {
    return hold('roster_coverage_split_window', 'roster');
  }

  let fallbackDates: string[];
  if (!hasPublishedCoverage) {
    fallbackDates = allDates;
  } else if (facts.startDate < coverageStart!) {
    fallbackDates = allDates.filter((date) => date < coverageStart!);
  } else if (facts.endDate > coverageEnd!) {
    fallbackDates = allDates.filter((date) => date > coverageEnd!);
  } else {
    // Inside a published roster period, an unassigned date is not rostered.
    fallbackDates = [];
  }
  const fallbackUnassignedDates = fallbackDates.filter(
    (date) => !phDates.has(date) && !assignmentsByDate.has(date),
  );
  if (fallbackUnassignedDates.length > 0 && contractedDaily === null) {
    return hold(
      'contracted_daily_hours_unverified',
      rosterRows.length > 0 ? 'roster' : 'pay_profile',
    );
  }
  const contractRows = fallbackDates
    .filter(
      (date) =>
        !phDates.has(date) &&
        !assignmentsByDate.has(date) &&
        profileWeekdays.includes(isoDayOfWeek(date)),
    )
    .map((date) => dayRow(date, contractedDaily!, 'pay_profile'));
  const contractedHours = contractRows.reduce((sum, row) => sum + row.hours, 0);
  const rosterUsed = hasPublishedCoverage || rosterRows.length > 0;
  const rows = [...rosterRows, ...contractRows].sort((left, right) =>
    left.date.localeCompare(right.date),
  );

  const hours = round2(rows.reduce((sum, row) => sum + row.hours, 0));
  const source: LeaveHoursSource =
    rosterHours > contractedHours
      ? 'roster'
      : contractedHours > rosterHours
        ? 'pay_profile'
        : rosterHours > 0
          ? 'pay_profile'
          : 'pay_profile';
  return {
    hours,
    source,
    perDay: rows,
    days: rows.length,
    businessDays,
    publicHolidayDates: [...phDates].sort(),
    hoursPerLeaveDay: rows.length > 0 ? round4(hours / rows.length) : legacyPerDay,
    blockedReason:
      hours <= 0 && allDates.some((date) => !phDates.has(date))
        ? 'no_ordinary_hours_in_window'
        : null,
    detail: {
      window: { start: facts.startDate, end: facts.endDate },
      precedence: ['roster_assignment_for_date', 'contracted_daily_hours', 'hold'],
      source_basis: {
        selected: source,
        roster_hours: round4(rosterHours),
        pattern_hours: round4(contractedHours),
        pattern_source: contractedHours > 0 ? 'pay_profile' : null,
      },
      roster: {
        shift_count: [...assignmentsByDate.values()].reduce((sum, value) => sum + value.length, 0),
        hours: round4(rosterHours),
        overtime_shifts_excluded: overtimeExcluded,
        coverage_start_date: coverageStart,
        horizon_date: coverageEnd,
        used: rosterUsed,
      },
      pattern:
        contractedDaily === null
          ? null
          : {
              contracted_hours_per_week: contracted,
              profile_ordinary_days_of_week: profileWeekdays,
              hours_per_day: contractedDaily,
              hours: round4(contractedHours),
            },
    },
  };
}

function resolveLeaveRequestHoursLegacy(facts: LeaveHoursResolutionFacts): LeaveHoursResolution {
  if (facts.endDate < facts.startDate) {
    throw new Error('leave_hours_resolution_window_invalid');
  }
  const phDates = new Set((facts.publicHolidayDates ?? []).map((d) => d.slice(0, 10)));
  const allDates = enumerateDates(facts.startDate, facts.endDate);
  const businessDays = allDates.filter((d) => isoDayOfWeek(d) <= 5).length;
  const contracted =
    facts.contractedHoursPerWeek !== null &&
    facts.contractedHoursPerWeek !== undefined &&
    facts.contractedHoursPerWeek > 0
      ? facts.contractedHoursPerWeek
      : null;
  const maxDaily =
    facts.maximumDailyOrdinaryHours !== null &&
    facts.maximumDailyOrdinaryHours !== undefined &&
    facts.maximumDailyOrdinaryHours > 0
      ? facts.maximumDailyOrdinaryHours
      : null;
  // Sanitised profile weekday set (sorted, distinct, ISO 1–7). The day COUNT
  // is the cardinality of that same set, never an independently-read number:
  // splitting the two is what let a Tue/Thu/Sat worker be divided by 3 and
  // counted Mon–Fri at the same time.
  const profileWeekdays = [
    ...new Set(
      (facts.ordinaryDaysOfWeek ?? []).filter(
        (dow) => Number.isInteger(dow) && dow >= 1 && dow <= 7,
      ),
    ),
  ].sort((a, b) => a - b);
  const profileDays =
    profileWeekdays.length > 0 ? profileWeekdays.length : (facts.ordinaryDaysOfWeekCount ?? 0);
  const employmentType = String(facts.employmentType ?? '').toUpperCase();
  const custom =
    employmentType === 'FULL_TIME' &&
    (facts.customFullTimeDailyOrdinaryHours === 8 || facts.customFullTimeDailyOrdinaryHours === 10)
      ? facts.customFullTimeDailyOrdinaryHours
      : null;
  const isCasual = employmentType === 'CASUAL';

  // Legacy per-day factor (mirrors resolve_worker_hours_per_leave_day).
  let legacyPerDay = HOURS_PER_LEAVE_DAY;
  let legacySource: LeaveHoursSource = 'default';
  if (contracted !== null && profileDays > 0) {
    legacyPerDay = round4(
      maxDaily !== null ? Math.min(contracted / profileDays, maxDaily) : contracted / profileDays,
    );
    legacySource = 'pay_profile';
  } else if (custom !== null) {
    legacyPerDay = custom;
    legacySource = 'custom_fields';
  }

  const reviewHold = (
    blockedReason: LeaveHoursBlockedReason,
    source: LeaveHoursSource = legacySource,
  ): LeaveHoursResolution => ({
    hours: 0,
    source,
    perDay: [],
    days: 0,
    businessDays,
    publicHolidayDates: [...phDates].sort(),
    hoursPerLeaveDay: round4(legacyPerDay),
    blockedReason,
    detail: {
      casual: isCasual,
      employment_type: facts.employmentType ?? null,
      window: { start: facts.startDate, end: facts.endDate },
      review_required: blockedReason,
    },
  });

  if (facts.publicHolidayWorkStateUnverified === true) {
    return reviewHold('public_holiday_work_state_unverified');
  }
  if (facts.publicHolidayWorkLocationUnverified === true) {
    return reviewHold('public_holiday_work_location_unverified');
  }
  if (
    (facts.rosterAssignments ?? []).some(
      (assignment) =>
        assignment.parentShiftDate != null && assignment.parentShiftDate !== assignment.date,
    )
  ) {
    return reviewHold('cross_midnight_slot_date_unverified', 'roster');
  }

  if (isCasual) {
    const rows = allDates
      .filter((d) => isoDayOfWeek(d) <= 5 && !phDates.has(d))
      .map((d) => dayRow(d, round4(legacyPerDay), legacySource));
    return {
      hours: round2(rows.length * legacyPerDay),
      source: legacySource,
      perDay: rows,
      days: rows.length,
      businessDays,
      publicHolidayDates: [...phDates].sort(),
      hoursPerLeaveDay: round4(legacyPerDay),
      blockedReason: legacySource === 'default' ? 'default_hours_unverified' : null,
      detail: { casual: true, employment_type: facts.employmentType ?? null },
    };
  }

  if (facts.profileTransitionInWindow === true) {
    return {
      hours: 0,
      source: 'pay_profile',
      perDay: [],
      days: 0,
      businessDays,
      publicHolidayDates: [...phDates].sort(),
      hoursPerLeaveDay: round4(legacyPerDay),
      blockedReason: 'pay_profile_transition_in_window',
      detail: {
        casual: false,
        employment_type: facts.employmentType ?? null,
        window: { start: facts.startDate, end: facts.endDate },
        review_required: 'pay_profile_transition_in_window',
      },
    };
  }

  // History sample: ordinary hours per day (capped), ≤ 8 most recent weeks.
  const sampledWeeks = [...(facts.historyWeeks ?? [])]
    .map((week) => {
      const byDay = new Map<string, number>();
      for (const day of week.days) {
        if (day.hours > 0) byDay.set(day.date, (byDay.get(day.date) ?? 0) + day.hours);
      }
      const dayHours = [...byDay.entries()].map(([date, hours]) => ({
        date,
        hours: maxDaily !== null ? Math.min(hours, maxDaily) : hours,
      }));
      return {
        weekStart: week.weekStart,
        hours: dayHours.reduce((sum, d) => sum + d.hours, 0),
        weekdays: [...new Set(dayHours.map((d) => isoDayOfWeek(d.date)))],
      };
    })
    .filter((week) => week.weekdays.length > 0)
    .sort((a, b) => (a.weekStart < b.weekStart ? 1 : a.weekStart > b.weekStart ? -1 : 0))
    .slice(0, LEAVE_HOURS_HISTORY_SAMPLE_WEEKS);
  const histWeeks = sampledWeeks.length;
  const histMedianHours = continuousMedian(sampledWeeks.map((w) => w.hours));
  const histMedianDays = continuousMedian(sampledWeeks.map((w) => w.weekdays.length));
  const histUsualWeekdays = histWeeks > 0 ? usualWeekdays(sampledWeeks) : [];
  const histWeekStarts = sampledWeeks.map((w) => w.weekStart).sort();

  if (
    !isCasual &&
    contracted !== null &&
    profileWeekdays.length === 0 &&
    !(histWeeks >= LEAVE_HOURS_HISTORY_MIN_WEEKS && histUsualWeekdays.length > 0)
  ) {
    return reviewHold('ordinary_weekday_pattern_unverified', 'pay_profile');
  }

  // The ONE usual weekday set (mirrors the SQL): profile ordinary_days_of_week
  // → roster-history usual weekdays (>= 3 sampled weeks). Mon–Fri is retained
  // only for non-contract pattern fallbacks; an incomplete weekly contract is
  // held above. Both the weekly divisor and counted days come from one set.
  let usualWeekdaySet: number[];
  let usualDaysSource: 'ordinary_hours_profile' | 'roster_history' | 'default_5';
  if (profileWeekdays.length > 0) {
    usualWeekdaySet = profileWeekdays;
    usualDaysSource = 'ordinary_hours_profile';
  } else if (histWeeks >= LEAVE_HOURS_HISTORY_MIN_WEEKS && histUsualWeekdays.length > 0) {
    usualWeekdaySet = histUsualWeekdays;
    usualDaysSource = 'roster_history';
  } else {
    usualWeekdaySet = [...WEEKDAYS];
    usualDaysSource = 'default_5';
  }
  const usualWeekdayCount = usualWeekdaySet.length;

  // Step 2 candidate: roster inside the window (only without an agreed pattern).
  let rosterUsed = false;
  const rosterRows: LeaveHoursResolutionDay[] = [];
  let rosterHours = 0;
  let rosterDays = 0;
  let rosterShiftCount = 0;
  let rosterCappedDays = 0;
  let rosterOvertimeExcluded = 0;
  let fbStart: string | null = null;
  let fbEnd: string | null = null;
  let fbAllowContract = true;

  if (contracted === null) {
    const inWindow = (facts.rosterAssignments ?? []).filter(
      (a) => a.date >= facts.startDate && a.date <= facts.endDate,
    );
    rosterOvertimeExcluded = inWindow.filter((a) => a.isOvertime === true).length;
    const ordinary = inWindow.filter(
      (a) => a.hours > 0 && a.isOvertime !== true && !phDates.has(a.date),
    );
    const byDay = new Map<string, LeaveHoursRosterAssignmentFact[]>();
    for (const a of ordinary) {
      const list = byDay.get(a.date) ?? [];
      list.push(a);
      byDay.set(a.date, list);
    }
    for (const [date, list] of [...byDay.entries()].sort(([a], [b]) => (a < b ? -1 : 1))) {
      const sorted = [...list].sort((a, b) =>
        String(a.startAt ?? '') < String(b.startAt ?? '') ? -1 : 1,
      );
      const raw = sorted.reduce((sum, a) => sum + rosterAssignmentLeaveHours(a), 0);
      const dayHours = maxDaily !== null ? Math.min(raw, maxDaily) : raw;
      const capped = dayHours < raw;
      if (capped) rosterCappedDays += 1;
      sorted.forEach((a, index) => {
        const assignmentHours = rosterAssignmentLeaveHours(a);
        const hours = capped ? (index === 0 ? round4(dayHours) : 0) : assignmentHours;
        rosterShiftCount += 1;
        if (hours <= 0) return;
        const linkable = a.provenanceOk === true && !capped;
        rosterRows.push({
          date,
          hours,
          source: 'roster',
          rosterShiftId: linkable ? (a.shiftId ?? null) : null,
          rosterAssignmentId: linkable ? (a.assignmentId ?? null) : null,
          slotId: linkable ? (a.slotId ?? null) : null,
          segmentStartAt: linkable ? (a.startAt ?? null) : null,
          segmentEndAt: linkable ? (a.endAt ?? null) : null,
          evidenceShiftId: a.shiftId ?? null,
          cappedToOrdinary: capped,
        });
      });
      rosterHours += dayHours;
      rosterDays += 1;
    }
    const coverageStart = facts.rosterCoverageStartDate ?? null;
    const coverageEnd = facts.rosterCoverageEndDate ?? facts.rosterHorizonDate ?? null;
    const hasPublishedCoverage = Boolean(
      coverageStart &&
      coverageEnd &&
      coverageStart <= facts.endDate &&
      coverageEnd >= facts.startDate,
    );
    if (hasPublishedCoverage || rosterShiftCount > 0) {
      rosterUsed = true;
      if ((facts.rosterCoverageGapCount ?? 0) > 0) {
        return {
          hours: 0,
          source: 'roster',
          perDay: [],
          days: 0,
          businessDays,
          publicHolidayDates: [...phDates].sort(),
          hoursPerLeaveDay: round4(legacyPerDay),
          blockedReason: 'roster_coverage_disjoint_window',
          detail: {
            casual: false,
            employment_type: facts.employmentType ?? null,
            window: { start: facts.startDate, end: facts.endDate },
            roster_coverage: {
              start: coverageStart,
              end: coverageEnd,
              gap_count: facts.rosterCoverageGapCount,
            },
            review_required: 'roster_coverage_disjoint_window',
          },
        };
      }
      if (
        coverageStart &&
        coverageEnd &&
        facts.startDate < coverageStart &&
        facts.endDate > coverageEnd
      ) {
        return {
          hours: 0,
          source: 'roster',
          perDay: [],
          days: 0,
          businessDays,
          publicHolidayDates: [...phDates].sort(),
          hoursPerLeaveDay: round4(legacyPerDay),
          blockedReason: 'roster_coverage_split_window',
          detail: {
            casual: false,
            employment_type: facts.employmentType ?? null,
            window: { start: facts.startDate, end: facts.endDate },
            roster_coverage: { start: coverageStart, end: coverageEnd },
            review_required: 'roster_coverage_split_window',
          },
        };
      }
      if (coverageStart && facts.startDate < coverageStart) {
        fbStart = facts.startDate;
        fbEnd = addDays(coverageStart, -1);
        fbAllowContract = false;
      } else if (coverageEnd && coverageEnd < facts.endDate) {
        fbStart = addDays(coverageEnd, 1);
        fbEnd = facts.endDate;
        fbAllowContract = false;
      }
    }
  }
  if (!rosterUsed) {
    fbStart = facts.startDate;
    fbEnd = facts.endDate;
  }

  let pattern: PatternResult | null = null;
  if (fbStart !== null && fbEnd !== null) {
    let source: LeaveHoursSource;
    let usualDays: number;
    let weekdays: number[];
    let perDay: number;
    let detail: Record<string, unknown>;
    if (fbAllowContract && contracted !== null) {
      // Step 1: contracted hours ÷ |usual weekday set|, counted over that SAME set.
      source = 'pay_profile';
      weekdays = usualWeekdaySet;
      usualDays = usualWeekdayCount;
      perDay = contracted / usualDays;
      if (maxDaily !== null) perDay = Math.min(perDay, maxDaily);
      detail = {
        contracted_hours_per_week: contracted,
        maximum_daily_ordinary_hours: maxDaily,
        usual_days_per_week: usualDays,
        usual_days_source: usualDaysSource,
        profile_ordinary_days_of_week: profileWeekdays,
        history_median_days_per_week: histMedianDays === null ? null : round4(histMedianDays),
      };
    } else if (
      histWeeks >= LEAVE_HOURS_HISTORY_MIN_WEEKS &&
      histMedianHours !== null &&
      histMedianHours > 0
    ) {
      // Step 3: median weekly hours ÷ |usual weekday set|, over that SAME set.
      source = 'roster_history';
      weekdays = usualWeekdaySet;
      usualDays = usualWeekdayCount;
      perDay = histMedianHours / usualDays;
      if (maxDaily !== null) perDay = Math.min(perDay, maxDaily);
      detail = {
        usual_hours_per_week: round4(histMedianHours),
        usual_days_per_week: usualDays,
        usual_days_source: usualDaysSource,
        profile_ordinary_days_of_week: profileWeekdays,
        history_median_days_per_week: histMedianDays === null ? null : round4(histMedianDays),
        weeks_used: histWeekStarts,
      };
    } else if (custom !== null) {
      // Steps 4–5 state a DAILY figure directly: no weekly divisor to keep
      // coherent with a counted-day set, so they keep the Mon–Fri business days.
      source = 'custom_fields';
      usualDays = 5;
      weekdays = [...WEEKDAYS];
      perDay = custom;
      detail = { full_time_daily_ordinary_hours: custom, usual_days_source: 'business_days' };
    } else {
      source = 'default';
      usualDays = 5;
      weekdays = [...WEEKDAYS];
      perDay = HOURS_PER_LEAVE_DAY;
      detail = { hours_per_day: HOURS_PER_LEAVE_DAY, usual_days_source: 'business_days' };
    }
    perDay = round4(perDay);
    const fbDates = enumerateDates(fbStart, fbEnd);
    const counted = fbDates.filter((d) => weekdays.includes(isoDayOfWeek(d)) && !phDates.has(d));
    let rows: LeaveHoursResolutionDay[];
    let hours: number;
    const proportional = false;
    if (counted.length > 0) {
      rows = counted.map((d) => dayRow(d, perDay, source));
      hours = round4(perDay * counted.length);
    } else {
      // No ordinary weekday overlap means no ordinary hours were lost. Never
      // manufacture a weekly/5 debit merely to satisfy a positive-hours
      // submit invariant; the BFF holds the full-day request for correction.
      rows = [];
      hours = 0;
    }
    pattern = {
      source,
      perDay,
      usualDays,
      weekdays,
      rows,
      hours,
      days: rows.length,
      countedDays: counted.length,
      proportional,
      detail: {
        ...detail,
        window: { start: fbStart, end: fbEnd },
        usual_weekdays: weekdays,
        counted_days: counted.length,
        proportional,
        hours_per_day: perDay,
        hours,
        source,
      },
    };
  }

  const perDayRows = rosterUsed ? [...rosterRows, ...(pattern?.rows ?? [])] : (pattern?.rows ?? []);
  const hours = rosterUsed ? rosterHours + (pattern?.hours ?? 0) : (pattern?.hours ?? 0);
  const days = rosterUsed ? rosterDays + (pattern?.days ?? 0) : (pattern?.days ?? 0);
  const blockedReason: LeaveHoursBlockedReason | null =
    hours <= 0 && allDates.some((date) => !phDates.has(date))
      ? 'no_ordinary_hours_in_window'
      : pattern?.source === 'default' && pattern.hours > 0
        ? 'default_hours_unverified'
        : null;
  // The top-level label names the basis that produced the MAJORITY of the
  // hours, not merely the first basis that fired. A window straddling the
  // roster horizon is a blend; calling the whole request 'roster' when most of
  // it was inferred from history over-claims the evidence. On an exact tie the
  // lower-precedence (more uncertain) basis wins. detail.source_basis carries
  // the split either way.
  const patternHours = pattern?.hours ?? 0;
  let source: LeaveHoursSource;
  if (!rosterUsed) {
    source = pattern?.source ?? 'default';
  } else if (!pattern || patternHours <= 0) {
    source = 'roster';
  } else if (patternHours > rosterHours) {
    source = pattern.source;
  } else if (rosterHours > patternHours) {
    source = 'roster';
  } else {
    source =
      leaveHoursSourcePrecedence(pattern.source) >= leaveHoursSourcePrecedence('roster')
        ? pattern.source
        : 'roster';
  }

  return {
    hours: round2(hours),
    source,
    perDay: perDayRows,
    days,
    businessDays,
    publicHolidayDates: [...phDates].sort(),
    hoursPerLeaveDay: days > 0 ? round4(hours / days) : round4(legacyPerDay),
    blockedReason,
    detail: {
      casual: false,
      employment_type: facts.employmentType ?? null,
      window: { start: facts.startDate, end: facts.endDate },
      public_holidays: { dates: [...phDates].sort() },
      ordinary_hours: {
        maximum_daily_ordinary_hours: maxDaily,
        contracted_hours_per_week: contracted,
      },
      // Why the top-level `source` says what it says, and the hours behind it.
      source_basis: {
        selected: source,
        rule: 'majority_hours;tie_to_lower_precedence',
        roster_hours: rosterUsed ? round4(rosterHours) : 0,
        pattern_hours: round4(patternHours),
        pattern_source: pattern?.source ?? null,
      },
      usual_weekdays: {
        weekdays: usualWeekdaySet,
        days: usualWeekdayCount,
        source: usualDaysSource,
        profile_ordinary_days_of_week: profileWeekdays,
      },
      roster: rosterUsed
        ? {
            shift_count: rosterShiftCount,
            days: rosterDays,
            hours: round4(rosterHours),
            overtime_shifts_excluded: rosterOvertimeExcluded,
            days_capped_to_ordinary: rosterCappedDays,
            coverage_start_date: facts.rosterCoverageStartDate ?? null,
            horizon_date: facts.rosterCoverageEndDate ?? facts.rosterHorizonDate ?? null,
            beyond_horizon:
              pattern && fbStart !== null
                ? {
                    from: fbStart,
                    to: fbEnd,
                    source: pattern.source,
                    days: pattern.days,
                    hours: pattern.hours,
                  }
                : null,
          }
        : null,
      history: {
        weeks: histWeeks,
        min_weeks: LEAVE_HOURS_HISTORY_MIN_WEEKS,
        sample_weeks: LEAVE_HOURS_HISTORY_SAMPLE_WEEKS,
        week_starts: histWeekStarts,
        median_hours_per_week: histMedianHours === null ? null : round4(histMedianHours),
        median_days_per_week: histMedianDays === null ? null : round4(histMedianDays),
        usual_weekdays: histUsualWeekdays,
      },
      pattern: pattern?.detail ?? null,
      legacy: { hours_per_leave_day: round4(legacyPerDay), source: legacySource },
    },
  };
}
